import DataSerie from './DataSerie'
import { getFileName } from './SaxsGeneralHelper'

/**
 * Frame data parser.
 * @param {*} csvData A CSV string containing the data for a list of frames. Each frame has 6 series of data.
 * The first serie of the CSV correspond to the X values which is common to all frames.
 * Then the next 6 consecutive series correspond to the frame1. The next 6 series correspond to frame 2 , etc.
 *
 * For one frame, the serie are (in the order):
 * - serie1, raw intensity
 * - serie2, stddev for raw intensity
 * - serie3, subtraction intensity
 * - serie4, stddev for subtraction intensity
 * - serie5, Buffer average
 * - serie6, stddev for buffer average
 *
 * Example:
 * q         , #811                ,#811_sub           ,#811_buffer_ave             ,#967                ,#967_sub          , #967_buffer_ave
 * 0.03750981,7.8975224,0.017306287,6.289631,0.09003535,7.673992,      , 0.002196171,7.820164,0.018035905,5.824997,0.1417639,7.673992         ,0.002196171
 */
export function CSVFrameToDataSeriesParser1(csvData) {
  const splitData = csvData.toString().split('\n')
  const splitFirstLine = splitData[0].trim().split(/\s*[\s,]\s*/)
  const splitSecondLine = splitData[1].trim().split(/\s*[\s,]\s*/)

  const frameCount = (splitSecondLine.length - 1) / 6 // 6 dataseries for 1 frameId
  const result = []

  for (let j = 0; j < frameCount; j++) {
    const frameName = splitFirstLine[j * 3 + 1]
    result.push({ dataSerie: new DataSerie(frameName + '_RawIntensity', [], [], '', frameName, false) })
    result.push({ dataSerie: new DataSerie(frameName + '_RawIntensityStdDev', [], [], '', '', true), errorIndex: 0 })
    result.push({
      dataSerie: new DataSerie(frameName + '_SubtractionIntensity', [], [], '', frameName + '_sub', false),
    })
    result.push({
      dataSerie: new DataSerie(frameName + '_SubtractionIntensityStdDev', [], [], '', '', true),
      errorIndex: 0,
    })
    result.push({
      dataSerie: new DataSerie(frameName + '_BufferAverage', [], [], '', frameName + '_buffer_ave', false),
    })
    result.push({ dataSerie: new DataSerie(frameName + '_BufferAverageStdDev', [], [], '', '', true), errorIndex: 0 })
  }

  for (let i = 0; i < splitData.length; i++) {
    const line = splitData[i].trim()
    const splitLine = line.split(/\s*[\s,]\s*/)

    if (i > 0) {
      for (let lineIndex = 1; lineIndex < splitLine.length; lineIndex++) {
        if (lineIndex % 2 !== 0) {
          // this is the case of dataseries corresponding to non std errors
          result[lineIndex - 1].dataSerie.pushPoint(Number(splitLine[0]), Number(splitLine[lineIndex]))
        }
        if (lineIndex % 2 === 0) {
          // this is the case of the dataserie corresponding to std errors. Dataserie is composed of point for the positive stddev (from x=0 to x=max)
          // followed by points for the negative stddev (from x=max to x=0)
          if (result[lineIndex - 1].errorIndex === 0) {
            result[lineIndex - 1].dataSerie.pushPoint(
              Number(splitLine[0]),
              Number(splitLine[lineIndex - 1]) + Number(splitLine[lineIndex])
            )
            result[lineIndex - 1].dataSerie.pushPoint(
              Number(splitLine[0]),
              Number(splitLine[lineIndex - 1]) - Number(splitLine[lineIndex])
            )
          } else {
            result[lineIndex - 1].dataSerie.addPointByIndex(
              Number(splitLine[0]),
              Number(splitLine[lineIndex - 1]) + Number(splitLine[lineIndex]),
              result[lineIndex - 1].errorIndex
            )
            result[lineIndex - 1].dataSerie.addPointByIndex(
              Number(splitLine[0]),
              Number(splitLine[lineIndex - 1]) - Number(splitLine[lineIndex]),
              result[lineIndex - 1].dataSerie.values.x.length - result[lineIndex - 1].errorIndex
            )
          }
          result[lineIndex - 1].errorIndex++
        }
      }
    }
  }

  const dataSeries = []
  result.forEach(element => {
    dataSeries.push(element.dataSerie.plotLiWorkAround())
  })

  return dataSeries
}

/**
 * Frame data parser.
 * @param {*} csvData A CSV string containing the data for a list of frames. Each frame has 4 series of data.
 * The first serie of the CSV correspond to the X values.
 * Then the next 4 consecutive series correspond to the frame 1. The next 4 series correspond to frame 2 , etc.
 *
 * For one frame, the serie are (in the order):
 * - serie1, raw intensity
 * - serie2, stddev for raw intensity
 * - serie3, ?
 * - serie4, ?
 *
 * Example
 *  q         ,       I,           ,        ,              Batch_046_00005.dat,Batch_046_00005.dat
 *  0.03750981,7.805933,-0.02556181,7.805933,-0.02556181
 */
export function CSVFrameToDataSeriesParser2(csvData, frame) {
  const splitData = csvData.toString().split('\n')

  const dataSerie1 = new DataSerie('rawIntensity', [], [], '', getFileName(frame.filePath, true), false)
  const dataSerieError1 = new DataSerie('rawIntensityStdDev', [], [], '', 'noLabelYet', true)

  let dataSerieError1Index = 0

  for (let i = 0; i < splitData.length; i++) {
    const line = splitData[i].trim()
    const splitLine = line.split(/\s*[\s,]\s*/)

    // splitLine.length === 3, handles the case of subtrated frame, average buffer frane and average sample frame
    // splitLine.length === 5, handles the case of the rest of frames
    if (splitLine.length === 5 || splitLine.length === 3) {
      dataSerie1.pushPoint(Number(splitLine[0]), Number(splitLine[1]))

      if (dataSerieError1Index === 0) {
        dataSerieError1.pushPoint(Number(splitLine[0]), Number(splitLine[1]) + Number(splitLine[2]))
        dataSerieError1.pushPoint(Number(splitLine[0]), Number(splitLine[1]) - Number(splitLine[2]))
      } else {
        dataSerieError1.addPointByIndex(
          Number(splitLine[0]),
          Number(splitLine[1]) + Number(splitLine[2]),
          dataSerieError1Index
        )
        dataSerieError1.addPointByIndex(
          Number(splitLine[0]),
          Number(splitLine[1]) - Number(splitLine[2]),
          dataSerieError1.values.x.length - dataSerieError1Index
        )
      }
      dataSerieError1Index++
    }
  }

  return [dataSerie1, dataSerieError1]
}
